/* eslint-disable functional/immutable-data */
import { Category, Item, Menu, Modifier, ModifierGroup } from 'olo-service';
import { MenuDataTransferable, UNCATEGORIZED } from './util';
import { fromCents } from 'util/Formatting';
import { TFunction } from 'i18next';

export function formatMenu(response: MenuDataTransferable, t: TFunction): Menu {
  // initialize modifier groups
  const modifierGroups: Record<string, ModifierGroup> = {};
  Object.keys(response.modifierGroups).forEach(modifierGroupId => {
    const rawGroup = response.modifierGroups[modifierGroupId];

    const minRequired = Math.max(0, rawGroup.minRequired ?? 0);
    const maxAllowed = Math.max(minRequired, rawGroup.maxAllowed ?? Number.MAX_SAFE_INTEGER);

    modifierGroups[modifierGroupId] = {
      ...rawGroup,
      minRequired,
      maxAllowed,
      modifiers: [],
    };
  });

  // process all the modifiers
  const modifiers: Record<string, Modifier> = {};
  response.modifiers
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .forEach(({ id, groupId, price, ...rawModifier }) => {
      const group = modifierGroups[groupId];

      if (!group) return;

      const modifier: Modifier = {
        ...rawModifier,
        id,
        price: fromCents(price),
        groupId,
      };

      group.modifiers.push(modifier);
      modifiers[id] = modifier;
    });

  // normalize minRequired and maxAllowed
  Object.values(modifierGroups).forEach(group => {
    const { minRequired, maxAllowed, modifiers: groupModifiers } = group;

    group.minRequired = Math.min(minRequired, groupModifiers.length);
    group.maxAllowed = Math.min(maxAllowed, groupModifiers.length);
  });

  // setup special catch all category
  const uncategorized: Category = {
    id : UNCATEGORIZED,
    name: t('uncategorized'),
    sortOrder: Number.MAX_SAFE_INTEGER,
    items: [],
  };

  // initialize categories
  const categories: Record<string, Category> = {};
  Object.keys(response.categories).forEach(categoryId => {
    const rawCategory = response.categories[categoryId];
    categories[categoryId] = {
      ...rawCategory,
      items: []
    };
  });

  // process all the items
  const items: Record<string, Item> = {};
  response.items.forEach(({ id, modifierGroupIds, categoryIds, price, ...rawItem }) => {
    const item: Item = {
      ...rawItem,
      id,
      categoryIds,
      price: fromCents(price),
      modifierGroups: [],
    };

    modifierGroupIds.forEach(modifierGroupId => {
      const modifierGroup = modifierGroups[modifierGroupId];

      if (!modifierGroup) return;

      item.modifierGroups.push(modifierGroup);
    });

    if (categoryIds.length === 0) {
      uncategorized.items.push(item);
      item.categoryIds.push(uncategorized.id);
    }

    items[id] = item;
  });

  // process each category's item ids (to preserve payload ordering)
  Object.keys(response.categories).forEach(categoryId => {
    const category = categories[categoryId];
    const rawCategory = response.categories[categoryId];

    if (!category || !rawCategory) return;

    rawCategory.items.forEach(itemId => {
      const item = items[itemId];

      if (!item) return;

      category.items.push(item);
    });
  });

  // backfill the categories with rest of items
  Object.values(items).forEach(item => {
    item.categoryIds.forEach(categoryId => {
      const category = categories[categoryId] ?? uncategorized;

      const index = category.items.findIndex(i => i === item);

      if (index > -1) return;

      category.items.push(item);
    });
  });

  const sortedCategories = Object.values(categories).sort((a, b) => a.sortOrder - b.sortOrder);

  return {
    items,
    categories: [...sortedCategories, uncategorized].filter(category => category.items.length > 0),
  };
}
